import { action, computed, makeAutoObservable, runInAction } from "mobx"
import { MenuItemModifierSet } from "../../types/types";
import MenuRestService from "../../services/menu/rest";
import {
    DeliveryType,
    Menu,
    MenuCategory,
    ItemBadge,
    MenuItem, ModifiersSetType,
    StaffCallKind,
    Table,
    WorkingScheduleDay
} from "../../services/menu/models";
import { rootStore } from "../index";
import moment, { Moment } from "moment";
import manifest from "./../manifest.json";
import {saveKeyToLocalStorage} from "./../persistence";
import { firstSlot } from "../../utils/workingSchedule";


class MenuViewStore {
    activeCategory: number = 21;
    //@ts-ignore
    menu: Menu = null;
    //@ts-ignore
    modifiersSet: { [s: string]: MenuItemModifierSet } = {};
    // Каталог бейджей заведения: выключенные бэк сюда не кладёт, поэтому неизвестный id
    // у позиции — это нормально, плашку просто не рисуем.
    badgesById: { [s: string]: ItemBadge } = {};
    categories: MenuCategory[] = [];

    //@ts-ignore
    items: { [s: string]: MenuItem[] } = {};
    tables: Table[] = [];

    isStaffCallWanted: boolean = false;
    calledStaffCall: StaffCallKind | null = null;
    selectedPopupItem?: MenuItem;
    infoMessageShowedAt: Date | null = null;

    isLoaded: boolean = false;

    status: {
        err: boolean,
        msg: string
    } = { err: false, msg: "" };

    // Не observable: это служебная защёлка загрузки, рендер от неё зависеть не должен.
    pendingInit: Promise<void> | null = null;

    constructor() {
        makeAutoObservable(this, { pendingInit: false });
    }


    get availableStaffCalls(): StaffCallKind[] {
        if (!rootStore.generalStore.tableId) return [];

        return this.menu.data.staffCalls || [];
    }

    get isChooseLangsEnabled(){
        return Object.values(this.menu.data.availableLanguages).filter(t => t.isEnabled).length > 1;
    }

    // Эффект в MenuView перезапускается на каждый рендер, пока isLoaded ещё false, — без этой
    // защёлки один и тот же запрос меню уходит на бэк по нескольку раз.
    async init(params: { menuId: number, organizationId: number, organizationBranchId: number }) {
        if (this.pendingInit) return this.pendingInit;

        this.pendingInit = this.loadMenu(params);
        try {
            await this.pendingInit;
        } finally {
            this.pendingInit = null;
        }
    }

    private async loadMenu(params: { menuId: number, organizationId: number, organizationBranchId: number }) {
        const res = await MenuRestService.getMenu(params.menuId);
        if (res) {
            runInAction(() => {
                if(!res.menu) return;
                this.menu = res.menu;
                this.modifiersSet = res.menuItemModifierSetsById;
                this.badgesById = res.menuItemBadgesById || {};
                this.items = Object.entries(res.menuItemsByCategoryId)
                    .map(([key, items]) => {
                        let res = items.filter(item => !item.data.isInStopList);
                        return [key, res] as [string, MenuItem[]];
                    }).reduce((memo, entry) => {
                        memo[entry[0]] = entry[1];
                        return memo;
                    }, {} as { [s: string]: MenuItem[] })
                this.activeCategory = res.menuCategories[0]?.id;
                this.categories = res.menuCategories.filter((category) => {
                    return this.items[category.id] && this.items[category.id].length > 0
                }).sort((item1, item2) => {
                    return item1.data.sortOrder - item2.data.sortOrder
                })
                this.tables = res.tables;
            });
            rootStore.generalStore.setIsMenuAvailable(
                res.menu && res.menu.data.isMenuEnabled && this.categories.length > 0
            );

            rootStore.generalStore.setIsAcceptingOrders(res.menu.data.isOrderCreationEnabled && (res.menu.data.deliverySettings[DeliveryType.toAddress]?.isEnabled ||
                res.menu.data.deliverySettings[DeliveryType.toTable]?.isEnabled ||
                res.menu.data.deliverySettings[DeliveryType.takeaway]?.isEnabled ||
                res.menu.data.deliverySettings[DeliveryType.takeawayToTime]?.isEnabled ||
                res.menu.data.deliverySettings[DeliveryType.toVehicle]?.isEnabled ||
                res.menu.data.deliverySettings[DeliveryType.insideTheRestaurant]?.isEnabled));

            rootStore.generalStore.setViewOnlyMode(!rootStore.generalStore.isAcceptingOrders);
            if(res.menu){
                rootStore.cartStore.setMenuId(res.menu.id)
                rootStore.cartStore.filterCart();
            }
        } else {
            rootStore.generalStore.setIsAcceptingOrders(false);
        }

        runInAction(() => this.isLoaded = true);
    }


    async callStaff (kind: StaffCallKind) {
        const table = this.getTableByPublicId(rootStore.generalStore.tableId);
        if(!table) return;
        try {
            await MenuRestService.callStaff(rootStore.generalStore.menuId, table.id, kind);

            runInAction(() => {
                this.calledStaffCall = kind;
            });
        } catch (e) {
            console.error(e);
        }
    }

    setCalledStaffCall(value: StaffCallKind | null){
        this.calledStaffCall = value;
    }

    @action
    setError(msg: string) {
        this.status.err = true;
        this.status.msg = msg;
    }


    @action
    checkingScheduleHours() {
        if (!this.menu) return;
        const today = moment().add(this.menu.data.workingSchedule.timezoneInMinutes, 'minutes');
        const weekBefore = today.clone().subtract(7, 'days');

        const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
        const daysMoment: {
            [s: number]: Moment
        } = [];
        let t = weekBefore.clone();
        while(t.toDate() <= today.toDate()){
            daysMoment[t.day()] = t;
            t = t.clone().add(1, 'day');
        }


        let slots: {
            from: Moment,
            to: Moment
        }[] = [];

        slots = Object.entries(this.menu.data.workingSchedule).filter(([day, item]) => {
            if(days.indexOf(day) < 0) return false;
            const schedule =  item as WorkingScheduleDay;
            return !schedule.isDayOff
        }, []).map(([day, schedule]: [string, WorkingScheduleDay]) => {
            const dayNumber = days.indexOf(day);
            const date = daysMoment[dayNumber];
            const slot = firstSlot(schedule);
            let from = date.clone().hours(slot.fromHour).minutes(slot.fromMinute).add(this.menu.data.workingSchedule.timezoneInMinutes, 'minutes')
            let to = date.clone().hours(slot.toHour).minutes(slot.toMinute).add(this.menu.data.workingSchedule.timezoneInMinutes, 'minutes');
            if(slot.toHour < slot.fromHour){
                to.add(1, 'day')
            }
            return {
                from, to
            };
        })

        const result = slots.filter(slot => {
            return today.toDate() >= slot.from.toDate() && today.toDate() <= slot.to.toDate()
        }).length

        return result > 0;
}


    @computed
    get workingTime(){
        return this.checkingScheduleHours();
    }


    @action
    setIsLoaded(value: boolean) {
        this.isLoaded = value;
    }


    @action
    setActiveCategory(categoryId: number, scroll: boolean = false) {
        this.activeCategory = categoryId;
        rootStore.generalMenuStore.scroll = scroll;
    }


    @computed
    getModifiersById(id: number) {

        return this.modifiersSet[id.toString()]
    }

    @action
    openModifiersPopup(item: MenuItem,  onOverlayClick: () => void) {
        rootStore.generalMenuStore.isModifiersPopupVisible = true;
        rootStore.generalMenuStore.isOverlayOpen = true;
        rootStore.generalMenuStore.onOverlayClick = onOverlayClick;
        this.selectedPopupItem = item;
        document.body.style.overflow = 'hidden';
    }

    @action
    closeModifiersPopup() {
        rootStore.generalMenuStore.isModifiersPopupVisible = false;
        rootStore.generalMenuStore.isOverlayOpen = false;
        rootStore.generalMenuStore.onOverlayClick = undefined;
        this.selectedPopupItem = undefined;
        document.body.style.overflow = 'auto';
    }


    @action
    openStaffCallModal( onOverlayClick: () => void) {
        this.isStaffCallWanted = true;
        rootStore.generalMenuStore.isOverlayOpen = true;
        rootStore.generalMenuStore.onOverlayClick = onOverlayClick;
        document.body.style.overflow = 'hidden';
    }
    @action
    closeStaffCallModal() {
        this.isStaffCallWanted = false;
        rootStore.generalMenuStore.isOverlayOpen = false;
        rootStore.generalMenuStore.onOverlayClick = undefined;
        document.body.style.overflow = 'auto';
    }


    @action
    setInfoMessageShowedAt(date: Date) {
        this.infoMessageShowedAt = date
        saveKeyToLocalStorage("infoMessageShowedAt", this.infoMessageShowedAt)
    }

    @computed
    getMinPrice(item: MenuItem) {
        let minPrice = 0;
        item.data.menuItemModifierSets.forEach((setId) => {
            const set = this.getModifiersById(setId.menuItemModifierSetId);
            let min = Number.MAX_SAFE_INTEGER;
            set.data.modifiers.forEach((item) => {
                if (item.data.sellPrice.price < min) {
                    min = item.data.sellPrice.price
                }
            })
            if(set.data.type === ModifiersSetType.ChooseOne){
                let {isRequired} = set.data

                if (isRequired) {
                    minPrice += min;
                }
            }else{
                let {minCountTotalChosen} = set.data;
                if(minCountTotalChosen > 0) minPrice += min;
            }
        });
        if (minPrice === Number.MAX_SAFE_INTEGER) return 0;
        return minPrice;
    }


    @computed
    getTableById(id: number) {
        return this.tables.find(item => item.id === id);
    }

    @computed
    getTableByPublicId(id: string) {
        return this.tables.find(item => item.data.publicId === parseInt(id));
    }

    @computed
    get storeFrontMode(){
        return this.menu.data.themeSettings.storefrontPhotosMode;
    }

    updateManifest(alias: string, name: string, description: string, logoUrl: string | null){
        const newManifest = {
            ...manifest,
            short_name: name,
            name: name,
            start_url: `${alias}`,
            icons: logoUrl ? [
                {
                    "src": logoUrl,
                    "sizes": "391x391",
                    "type": "image/jpeg"
                }
            ] : manifest.icons
        }
        const stringManifest = JSON.stringify(newManifest);
        const blob = new Blob([stringManifest], {type: 'application/json'});
        const manifestURL = URL.createObjectURL(blob);
        document.getElementById('my-manifest-placeholder')?.setAttribute('href', manifestURL);
        document.title = name;
        const descriptionTag = document.getElementById('description-placeholder');
        descriptionTag && (descriptionTag.innerText = description);
        if(logoUrl){
            document.getElementById('apple-touch-icon')?.setAttribute('href',  logoUrl);
        }
    }


    updateSeoInfo(params: {name: string, description: string}){
        document.title = `${params.name} - меню`;
        const descriptionTag = document.getElementById('description-placeholder');
        descriptionTag && (descriptionTag.innerText = `${params.name} - ${params.description}`);
    }


}


export default MenuViewStore
